import { CustomTableColumn } from '@hefe/models';
import { FormField } from '../../core/components/form/custom-form';

/**
 * Base interface for CRUD entities
 * All entities must have at least an id
 */
export interface CrudEntity {
  id: number;
}

/**
 * Context passed to column render functions for child relations
 */
export interface ChildColumnContext {
  parentResource: string;
  parentId: number;
  relation: string;
}

/**
 * Configuration for a child relation (nested CRUD)
 * Used for 1:N relationships like Partner -> Features
 *
 * @template C - The child entity type
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ChildRelationConfig<C extends { id: number } = any> {
  /** Relation name / endpoint (e.g., 'features') */
  relation: string;

  /** i18n key for add/edit/delete translations */
  i18nKey?: string;

  /** Display name singular for translations (e.g., 'Feature') */
  displayName: string;

  /** Key to use as modal title when editing child (e.g., 'name') */
  editTitleKey?: keyof C;

  /** Allow deleting multiple items at once */
  multiDelete?: boolean;

  /** Table columns for the child table */
  columns: CustomTableColumn[];

  /**
   * Optional: Function to get columns with parent context (for things like file uploads)
   * If provided, this takes precedence over columns
   */
  getColumns?: (context: ChildColumnContext) => CustomTableColumn[];

  /** Form fields for create/edit */
  formFields: FormField[];

  /** Optional: Different form fields for editing */
  editFormFields?: FormField[];
}

/**
 * Configuration for a CRUD resource
 *
 * Uses standard CustomTableColumn and FormField types directly -
 * no extra abstraction layer, same structure as manual implementations like UsersComponent.
 *
 * @template T - The entity type (e.g., Partner, Car, etc.)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface CrudConfig<T extends { id: number } = any> {
  /** API resource endpoint name (e.g., 'partners', 'cars') */
  resource: string;

  /** Display name singular for translations (e.g., 'partner') */
  displayName: string;

  /** Key to use as modal title when editing (e.g., 'name' shows the item's name) */
  editTitleKey?: keyof T;

  /** Allow deleting multiple items at once */
  multiDelete?: boolean;

  /** Table columns - standard CustomTableColumn array, same as UsersComponent */
  columns: CustomTableColumn[];

  /** Form fields for create - standard FormField array */
  formFields: FormField[];

  /**
   * Optional: Different form fields for editing
   * If not provided, formFields will be used with initialValues from the item
   */
  editFormFields?: FormField[];

  /**
   * Child relations for nested CRUD (e.g., Partner -> Features)
   * Each relation gets its own table within the edit modal
   */
  childRelations?: ChildRelationConfig[];
}

/**
 * Helper to create form fields with initial values from an existing item.
 * Used when editing - populates the form with the item's current values.
 */
export function getFormFieldsWithValues<T extends { id: number }>(
  fields: FormField[],
  item: T
): FormField[] {
  return fields.map((field) => ({
    ...field,
    initalValue: item[field.name as keyof T] ?? field.initalValue,
  }));
}
